import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { consequenceTypesService } from '@/services/consequenceTypesService';
import { mentionTypesService } from '@/services/mentionTypesService';
import { useNotifications } from '@/components/notifications/NotificationSystem';
import { CSS_CLASSES } from '@/utils/constants';
import { LoadingSpinner, ConfirmDialog } from '@/components/shared/UIComponents';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { commitProtectedDomainDefinitionConfiguration } from '@/services/protectedDomainDefinitionConfiguration';
import PhaseApplicabilityField from '@/features/phases/components/PhaseApplicabilityField';
import usePhaseConfigurationView from '@/features/phases/usePhaseConfigurationView';
import { useDomainStore } from '@/stores/domainStore';
import PhaseScopeSelector from '@/components/ui/PhaseScopeSelector';
import { reorderVisibleDefinitions } from '@/features/phases/phaseConfigurationView';
import { normalizePhaseApplicability } from '@/services/domain/phaseApplicability';
import { useSchool } from '@/contexts/SchoolContext';
import { useMode } from '@/contexts/ContextModeContext';
import SortableConsequenceTypeTag from './SortableConsequenceTypeTag';
import SortableExpandableConsequenceCard from './SortableExpandableConsequenceCard';
import IconPicker from '@/components/ui/IconPicker';
import ColorPicker from '@/components/ui/ColorPicker';
import { Button, Input } from '@/components/ui';
import { getIconByName } from '@/utils/iconMapping';
import AvailabilityPolicyEditor from './AvailabilityPolicyEditor';
import {
  createAvailabilityPolicy,
  normalizeAvailabilityPolicy,
  validateAvailabilityPolicyForForm,
} from '../utils/availabilityPolicy';
import {
  ACTION_ROSTER_MODES,
  ACTION_SESSION_GROUPINGS,
  createActionPolicy,
  normalizeActionPolicy,
} from '@/utils/discipline/actionRoster';

// Map consequence type values to Heroicon names
// This is needed because the DB stores emoji icons but the UI uses Heroicons
const TYPE_TO_ICON_MAP = {
  'demerit': 'ExclamationTriangleIcon',
  'warning': 'ExclamationTriangleIcon',
  'detention': 'ClockIcon',
  'homework-detention': 'BookOpenIcon',
  'friday-detention': 'CalendarDaysIcon',
  'saturday-detention': 'CalendarIcon',
  'suspension': 'NoSymbolIcon',
  'privilege-loss': 'LockClosedIcon',
  'referral': 'DocumentTextIcon',
  'contract': 'DocumentIcon',
  'restorative': 'HandRaisedIcon',
  // Mention types
  'homework': 'BookOpenIcon',
  'behavior': 'ExclamationTriangleIcon',
  'attendance': 'ClockIcon',
  'uniform': 'IdentificationIcon',
  'technology': 'ComputerDesktopIcon'
};

// Color map for displaying names instead of hex
// NOTE: Hex colors are stored in database, so keys must remain hex for lookup compatibility
/* eslint-disable no-restricted-syntax */
const COLOR_NAMES = {
  '#EF4444': 'Red',
  '#F97316': 'Orange',
  '#D97706': 'Amber',
  '#EAB308': 'Yellow',
  '#22C55E': 'Green',
  '#10B981': 'Emerald',
  '#14B8A6': 'Teal',
  '#06B6D4': 'Cyan',
  '#0EA5E9': 'Sky',
  '#3B82F6': 'Blue',
  '#6366F1': 'Indigo',
  '#A78BFA': 'Violet',
  '#A855F7': 'Purple',
  '#D946EF': 'Fuchsia',
  '#EC4899': 'Pink',
  '#F43F5E': 'Rose',
  '#000000': 'Black',
  '#374151': 'Dark Gray',
  '#6B7280': 'Gray',
  '#D1D5DB': 'Light Gray',
};
/* eslint-enable no-restricted-syntax */

const SEVERITY_OPTIONS = ['low', 'medium', 'high'];

export default function ConsequenceTypesManager({
  embedded = false,
  domain = null,
  mode: modeProp = null,
  visibleSections = 'all',
} = {}) {
  const [consequenceTypes, setConsequenceTypes] = useState([]);
  const [mentionTypes, setMentionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null); // Track which collection we're editing
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const { showError, showSuccess, showWarning } = useNotifications();
  const { schoolId, school, permissions } = useSchool();
  const { confirmDialog, showConfirm } = useConfirmDialog();

  const { mode: contextMode } = useMode();
  const resolvedMode = modeProp
    || (domain?.id === 'recognition' ? 'recognition' : null)
    || (domain?.id === 'discipline' ? 'mention' : null)
    || contextMode;
  const isRecognitionMode = resolvedMode === 'recognition';
  const showTypes = visibleSections === 'all' || visibleSections === 'types';
  const showActions = visibleSections === 'all' || visibleSections === 'actions';

  const definitionDomainId = isRecognitionMode ? 'recognition' : 'discipline';
  const domains = useDomainStore(state => state.domains);
  const { filter: filterDefinitions } = usePhaseConfigurationView(
    domain ?? domains?.find(candidate => candidate.id === definitionDomainId));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const createIdRef = useRef(null);
  const quickCreateRef = useRef(null);
  const writeDefinition = (operation, kind, fields = {}) => commitProtectedDomainDefinitionConfiguration({
    operation, schoolId, domainId: definitionDomainId, kind, ...fields,
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Form state
  // TODO: hex-colour — refactor color system to use DaisyUI semantic classes
  // Currently stores hex values in database and uses them in inline styles.
  // Proper fix requires schema migration + ColorPicker refactor.
  const [typeForm, setTypeForm] = useState({
    value: '',
    label: '',
    singular: '',
    plural: '',
    icon: 'ExclamationTriangleIcon',
    color: '#F59E0B', // eslint-disable-line no-restricted-syntax
    description: '',
    severity: 'medium',
    requiresAction: false,
    requiresAdjudication: false,
    isDefault: false,
    requiredContextFields: [],
    sortOrder: 0,
    actionPolicy: createActionPolicy(),
    availabilityPolicy: createAvailabilityPolicy()
  });

  const canEditAvailability =
    school?.featureFlags?.actionAvailabilityLiveStatus === true &&
    (permissions?.admin === true || permissions?.dev === true);

  const loadTypes = useCallback(async () => {
    try {
      setLoading(true);
      const [consequenceTypesData, mentionTypesData] = await Promise.all([
        consequenceTypesService.getAllIncludingInactive(schoolId),
        mentionTypesService.getAllIncludingInactive(schoolId)
      ]);
      setConsequenceTypes(consequenceTypesData.filter(type => type.retired !== true));
      setMentionTypes(mentionTypesData.filter(type => type.retired !== true));
    } catch (error) {
      console.error('Error loading types:', error);
      showError('Failed to load types');
    } finally {
      setLoading(false);
    }
  }, [schoolId, showError]);

  // Load data on component mount
  useEffect(() => {
    if (schoolId) {
      loadTypes();
    }
  }, [schoolId, loadTypes]);

  // Filter types by mode (discipline vs recognition) then by search
  const modeFilteredMentionTypes = useMemo(() => {
    return mentionTypes.filter(type =>
      isRecognitionMode ? type.mode === 'recognition' : type.mode !== 'recognition'
    );
  }, [mentionTypes, isRecognitionMode]);

  const modeFilteredConsequenceTypes = useMemo(() => {
    return consequenceTypes.filter(type =>
      isRecognitionMode ? type.mode === 'recognition' : type.mode !== 'recognition'
    );
  }, [consequenceTypes, isRecognitionMode]);

  const filteredMentionTypes = useMemo(() => {
    const visible = filterDefinitions(modeFilteredMentionTypes);
    if (!searchQuery.trim()) return visible;

    const query = searchQuery.toLowerCase();
    return visible.filter(type =>
      type.value?.toLowerCase().includes(query) ||
      type.label?.toLowerCase().includes(query) ||
      type.description?.toLowerCase().includes(query)
    );
  }, [modeFilteredMentionTypes, searchQuery, filterDefinitions]);

  const filteredConsequenceTypes = useMemo(() => {
    const visible = filterDefinitions(modeFilteredConsequenceTypes);
    if (!searchQuery.trim()) return visible;

    const query = searchQuery.toLowerCase();
    return visible.filter(type =>
      type.value?.toLowerCase().includes(query) ||
      type.label?.toLowerCase().includes(query) ||
      type.singular?.toLowerCase().includes(query) ||
      type.plural?.toLowerCase().includes(query) ||
      type.description?.toLowerCase().includes(query)
    );
  }, [modeFilteredConsequenceTypes, searchQuery, filterDefinitions]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    if (!typeForm.value.trim()) {
      showWarning('Value is required');
      return;
    }

    if (!typeForm.label.trim()) {
      showWarning('Label is required');
      return;
    }

    if (editingCollection !== 'mention' && canEditAvailability && typeForm.requiresAction) {
      const policyErrors = validateAvailabilityPolicyForForm(typeForm.availabilityPolicy);
      if (policyErrors.length > 0) {
        showWarning(policyErrors[0]);
        return;
      }
    }

    setSaving(true);
    setSaveError(null);
    try {
      // For new types, place at top by finding max sortOrder and adding 1
      let sortOrder = 0;
      if (!editingType) {
        const allTypes = editingCollection === 'mention' ? modeFilteredMentionTypes : modeFilteredConsequenceTypes;
        if (allTypes.length > 0) {
          const maxSortOrder = Math.max(...allTypes.map(t => t.sortOrder ?? 0));
          sortOrder = maxSortOrder + 1;
        }
      }

      // Build type data - mentions don't need severity/requiresAction
      const typeData = {
        value: editingType?.value || typeForm.value.trim().toLowerCase(),
        label: typeForm.label.trim(),
        icon: typeForm.icon,
        color: typeForm.color,
        description: typeForm.description.trim(),
        sortOrder: editingType ? (editingType.sortOrder ?? 0) : sortOrder,
        active: editingType ? editingType.active !== false : true,
        phaseApplicability: normalizePhaseApplicability(typeForm.phaseApplicability)
      };

      // Add mention-specific fields
      if (editingCollection === 'mention') {
        typeData.isDefault = typeForm.isDefault;
        typeData.requiredContextFields = typeForm.requiredContextFields;
      }

      // Add consequence-specific fields
      if (editingCollection !== 'mention') {
        typeData.singular = typeForm.singular.trim() || typeForm.value.trim().toLowerCase();
        typeData.plural = typeForm.plural.trim() || typeForm.value.trim().toLowerCase() + 's';
        typeData.severity = typeForm.severity;
        typeData.requiresAction = typeForm.requiresAction;
        typeData.requiresAdjudication = typeForm.requiresAdjudication;
        typeData.actionPolicy = normalizeActionPolicy(typeForm.actionPolicy);
        if (canEditAvailability) {
          typeData.availabilityPolicy = normalizeAvailabilityPolicy(
            {
              ...typeForm.availabilityPolicy,
              enabled: typeForm.requiresAction && typeForm.availabilityPolicy.enabled === true,
            },
            editingType?.availabilityPolicy || null
          );
        }
      }

      const typeName = editingCollection === 'mention'
        ? (isRecognitionMode ? 'Recognition type' : 'Mention type')
        : (isRecognitionMode ? 'Reward type' : 'Consequence type');

      await writeDefinition(editingType ? 'update' : 'create', editingCollection === 'mention' ? 'event-type' : 'action', {
        definitionId: editingType?.id || createIdRef.current,
        configuration: typeData,
        ...(editingType ? { expectedRevision: editingType.configurationRevision || 0 } : {}),
      });
      showSuccess(`${typeName} ${editingType ? 'updated' : 'created'} successfully`);

      // Reload types and close modal
      await loadTypes();
      setShowModal(false);
    } catch (error) {
      setSaveError({ message: error.message || 'Failed to save definition', stale: ['aborted', 'functions/aborted'].includes(error.code) });
    } finally { setSaving(false); }
  };

  // Handle deletion
  const handleDelete = async (type) => {
    const confirmed = await showConfirm({
      title: 'Retire this definition?',
      message: `Retire "${type.label}"? Its history will remain. Active rules must use an available definition.`,
      variant: 'danger',
      confirmText: 'Retire'
    });
    if (!confirmed) {
      return;
    }

    try {
      const typeName = editingCollection === 'mention'
        ? (isRecognitionMode ? 'Recognition type' : 'Mention type')
        : (isRecognitionMode ? 'Reward type' : 'Consequence type');

      await writeDefinition('retire', editingCollection === 'mention' ? 'event-type' : 'action', {
        definitionId: type.id, expectedRevision: type.configurationRevision || 0,
      });
      showSuccess(`${typeName} retired successfully`);
      await loadTypes();
      closeModal();
    } catch (error) {
      console.error('Error deleting type:', error);
      setSaveError({ message: error.message || 'Failed to retire definition', stale: ['aborted', 'functions/aborted'].includes(error.code) });
    }
  };

  // Modal handlers
  const openModal = (type = null, collection = 'consequence') => {
    setSaveError(null);
    createIdRef.current = crypto.randomUUID();
    if (type) {
      setEditingType(type);
      setEditingCollection(collection);
      // Map the icon - if it's an emoji from DB, map it to a Heroicon name
      const iconName = TYPE_TO_ICON_MAP[type.value] ||
                      (type.icon && type.icon.endsWith('Icon') ? type.icon : 'ExclamationTriangleIcon');
      setTypeForm({
        phaseApplicability: type.phaseApplicability ?? { mode: 'whole-school', phaseIds: [] },
        value: type.value || '',
        label: type.label || '',
        singular: type.singular || '',
        plural: type.plural || '',
        icon: iconName, // Use Heroicon name, NOT emoji
        color: type.color || '#F59E0B', // eslint-disable-line no-restricted-syntax
        description: type.description || '',
        severity: type.severity || 'medium',
        requiresAction: type.requiresAction || false,
        requiresAdjudication: type.requiresAdjudication || false,
        isDefault: type.isDefault || false,
        requiredContextFields: Array.isArray(type.requiredContextFields)
          ? type.requiredContextFields
          : [],
        sortOrder: type.sortOrder || 0,
        actionPolicy: normalizeActionPolicy(type.actionPolicy),
        availabilityPolicy: type.availabilityPolicy || createAvailabilityPolicy(school?.timezone || 'Africa/Johannesburg')
      });
    } else {
      setEditingType(null);
      setEditingCollection(collection);
      setTypeForm({
        phaseApplicability: { mode: 'whole-school', phaseIds: [] },
        value: '',
        label: '',
        singular: '',
        plural: '',
        icon: 'ExclamationTriangleIcon',
        color: '#F59E0B', // eslint-disable-line no-restricted-syntax
        description: '',
        severity: 'medium',
        requiresAction: false,
        requiresAdjudication: false,
        isDefault: false,
        requiredContextFields: [],
        sortOrder: 0,
        actionPolicy: createActionPolicy(),
        availabilityPolicy: createAvailabilityPolicy(school?.timezone || 'Africa/Johannesburg')
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingType(null);
    setEditingCollection(null);
  };

  const clearFilters = () => {
    setSearchQuery('');
  };

  const handleQuickAdd = async (e) => {
    e?.preventDefault();
    const name = quickAddName.trim();
    if (!name || quickAddSubmitting) return;

    try {
      setQuickAddSubmitting(true);
      const value = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (quickCreateRef.current?.name !== name) quickCreateRef.current = { name, id: crypto.randomUUID() };
      await writeDefinition('create', 'action', { definitionId: quickCreateRef.current.id, configuration: {
        value,
        label: name,
        singular: value,
        plural: `${value}s`,
        icon: 'BoltIcon',
        // eslint-disable-next-line no-restricted-syntax
        color: '#06B6D4',
        description: '',
        severity: 'medium',
        requiresAction: false,
        requiresAdjudication: false,
        sortOrder: 0,
        active: true,
      } });
      quickCreateRef.current = null;
      setQuickAddName('');
      await loadTypes();
      showSuccess('Action created');
    } catch (error) {
      console.error('Error quick-creating action:', error);
      showError(error.message || 'Failed to create action');
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  // Handle drag end for reordering types (within their respective groups)
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Determine which group we're reordering within
      const isMentionType = filteredMentionTypes.some(t => t.id === active.id);
      const groupTypes = isMentionType ? filteredMentionTypes : filteredConsequenceTypes;

      const oldIndex = groupTypes.findIndex(type => type.id === active.id);
      const newIndex = groupTypes.findIndex(type => type.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedTypes = reorderVisibleDefinitions(isMentionType ? modeFilteredMentionTypes : modeFilteredConsequenceTypes,
          groupTypes.map(type => type.id), active.id, over.id);

        // Calculate new sort orders - highest number at top
        const updates = reorderedTypes.map((type, index) => ({
          id: type.id,
          sortOrder: reorderedTypes.length - index,
          expectedRevision: type.configurationRevision || 0
        }));

        // Optimistically update UI
        const groupIds = new Set(reorderedTypes.map(type => type.id));
        const reorderGroup = previous => {
          let index = 0;
          return previous.map(type => groupIds.has(type.id) ? reorderedTypes[index++] : type);
        };
        if (isMentionType) setMentionTypes(reorderGroup);
        else setConsequenceTypes(reorderGroup);

        // Update Firebase with new sort orders
        try {
          await writeDefinition('reorder', isMentionType ? 'event-type' : 'action', { orders: updates });
          await loadTypes(); // Reload to get fresh data
        } catch (error) {
          console.error('Error updating type order:', error);
          showError(`Failed to update type order: ${error.message || 'Unknown error'}`);
          // Reload types to reset order
          await loadTypes();
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      {!embedded && <div className="flex justify-end"><PhaseScopeSelector /></div>}
      {/* Modal */}
      {typeof document !== 'undefined' && createPortal(<AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[100] flex bg-black/50 backdrop-blur-sm ${
              embedded
                ? 'items-stretch justify-stretch p-0 sm:items-center sm:justify-center sm:p-4'
                : 'items-center justify-center p-4'
            }`}
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full overflow-y-auto bg-base-100 shadow-xl ${
                embedded
                  ? 'h-[100dvh] max-h-[100dvh] max-w-none rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-xl'
                  : 'max-h-[90vh] max-w-lg rounded-xl'
              }`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="domain-type-editor-title"
            >
              <div className={embedded ? 'p-4 sm:p-6' : 'p-6'}>
                <div className="flex items-center justify-between mb-6">
                  <h2 id="domain-type-editor-title" className="text-xl font-bold sm:text-2xl">
                    {editingCollection === 'mention'
                      ? (editingType
                        ? (isRecognitionMode ? 'Edit Recognition Type' : 'Edit Mention Type')
                        : (isRecognitionMode ? 'Create Recognition Type' : 'Create Mention Type'))
                      : embedded
                        ? (editingType ? 'Edit Action' : 'Create Action')
                        : (editingType
                          ? (isRecognitionMode ? 'Edit Reward Type' : 'Edit Consequence Type')
                          : (isRecognitionMode ? 'Create Reward Type' : 'Create Consequence Type'))
                    }
                  </h2>
                  <Button
                    type="button"
                    onClick={closeModal}
                    className="btn-ghost btn-circle btn-sm min-h-11 min-w-11 sm:min-h-9 sm:min-w-9"
                    aria-label="Close type editor"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <fieldset disabled={saving} className="space-y-4">
                  {/* Label (Required) */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">
                        Name <span className="text-error">*</span>
                      </span>
                    </label>
                    <Input
                      type="text"
                      placeholder={isRecognitionMode ? "e.g., Academic Excellence" : "e.g., Homework Mention"}
                      value={typeForm.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        const value = editingType?.value || label.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                        setTypeForm(prev => ({ ...prev, label, value }));
                      }}
                      className="input-bordered w-full"
                      required
                      autoFocus
                    />
                  </div>

                  {/* Singular & Plural - only for consequences */}
                  {editingCollection !== 'mention' && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="label">
                          <span className="label-text font-medium">Singular</span>
                        </label>
                        <Input
                          type="text"
                          placeholder={isRecognitionMode ? "e.g., merit" : "e.g., detention"}
                          value={typeForm.singular}
                          onChange={(e) => setTypeForm(prev => ({ ...prev, singular: e.target.value }))}
                          className="input-bordered w-full"
                        />
                      </div>
                      <div>
                        <label className="label">
                          <span className="label-text font-medium">Plural</span>
                        </label>
                        <Input
                          type="text"
                          placeholder={isRecognitionMode ? "e.g., merits" : "e.g., detentions"}
                          value={typeForm.plural}
                          onChange={(e) => setTypeForm(prev => ({ ...prev, plural: e.target.value }))}
                          className="input-bordered w-full"
                        />
                      </div>
                    </div>
                  )}

                  {/* Icon */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Icon</span>
                    </label>
                    <Button
                      type="button"
                      onClick={() => setShowIconPicker(true)}
                      className="btn-outline justify-start space-x-2 w-full"
                    >
                      {getIconByName(typeForm.icon, "w-5 h-5")}
                      <span className="ml-2">{typeForm.icon?.replace('Icon', '')}</span>
                      <PhotoIcon className="w-4 h-4 ml-auto opacity-60" />
                    </Button>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Color</span>
                    </label>
                    <Button
                      type="button"
                      onClick={() => setShowColorPicker(true)}
                      className="btn-outline justify-start space-x-2 w-full"
                    >
                      <div
                        className="w-5 h-5 rounded border border-base-300"
                        style={{ backgroundColor: typeForm.color }}
                      />
                      <span>{COLOR_NAMES[typeForm.color] || 'Custom Color'}</span>
                    </Button>
                  </div>

                  {/* Severity - only for legacy discipline consequences */}
                  {editingCollection !== 'mention' && (
                    <div>
                      <label className="label">
                        <span className="label-text font-medium">Severity</span>
                      </label>
                      <select
                        value={typeForm.severity}
                        onChange={(e) => setTypeForm(prev => ({ ...prev, severity: e.target.value }))}
                        className="select select-bordered w-full"
                      >
                        {SEVERITY_OPTIONS.map(severity => (
                          <option key={severity} value={severity}>{severity}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Requires Action - only for consequences */}
                  {editingCollection !== 'mention' && (
                    <div>
                      <label className="flex cursor-pointer items-start gap-3 py-2">
                        <input
                          type="checkbox"
                          checked={typeForm.requiresAction}
                          onChange={(e) => setTypeForm(prev => ({ ...prev, requiresAction: e.target.checked }))}
                          className="checkbox checkbox-primary mt-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="block font-medium">Requires Action</span>
                          <div className="mt-1 break-words text-sm text-base-content/60">{isRecognitionMode ? 'Does this reward require scheduling or follow-up?' : 'Does this consequence require scheduling or follow-up?'}</div>
                        </div>
                      </label>
                    </div>
                  )}

                  {editingCollection !== 'mention' && typeForm.requiresAction && canEditAvailability && (
                    <AvailabilityPolicyEditor
                      value={typeForm.availabilityPolicy}
                      onChange={(availabilityPolicy) => setTypeForm(prev => ({ ...prev, availabilityPolicy }))}
                    />
                  )}

                  {editingCollection !== 'mention' && typeForm.requiresAction && (
                    <div>
                      <label className="label" htmlFor="action-roster-mode">
                        <span className="label-text font-medium">Session roster rows</span>
                      </label>
                      <select
                        id="action-roster-mode"
                        value={normalizeActionPolicy(typeForm.actionPolicy).rosterMode}
                        onChange={(e) => setTypeForm(prev => ({
                          ...prev,
                          actionPolicy: {
                            ...normalizeActionPolicy(prev.actionPolicy),
                            rosterMode: e.target.value,
                          },
                        }))}
                        className="select select-bordered w-full"
                      >
                        <option value={ACTION_ROSTER_MODES.PER_LEARNER}>
                          One row per learner
                        </option>
                        <option value={ACTION_ROSTER_MODES.PER_OBLIGATION}>
                          One row per obligation
                        </option>
                      </select>
                      <p className="mt-1 text-xs text-base-content/60">
                        Per learner uses the oldest outstanding obligation for this session and keeps later obligations waiting.
                      </p>
                    </div>
                  )}

                  {editingCollection !== 'mention' && typeForm.requiresAction && (
                    <div>
                      <label className="label" htmlFor="action-session-scope">
                        <span className="label-text font-medium">Run each Session for</span>
                      </label>
                      <select
                        id="action-session-scope"
                        value={normalizeActionPolicy(typeForm.actionPolicy).sessionGrouping}
                        onChange={(e) => setTypeForm(prev => ({
                          ...prev,
                          actionPolicy: {
                            ...normalizeActionPolicy(prev.actionPolicy),
                            sessionGrouping: e.target.value,
                          },
                        }))}
                        className="select select-bordered w-full"
                      >
                        <option value={ACTION_SESSION_GROUPINGS.CLASS}>One class at a time</option>
                        <option value={ACTION_SESSION_GROUPINGS.PHASE}>One phase at a time</option>
                        <option value={ACTION_SESSION_GROUPINGS.SCOPE}>Staff member&apos;s scope</option>
                        <option value={ACTION_SESSION_GROUPINGS.SCHOOL}>Whole school</option>
                      </select>
                    </div>
                  )}

                  {/* Requires Adjudication - only for consequences */}
                  {editingCollection !== 'mention' && !isRecognitionMode && (
                    <div>
                      <label className="flex cursor-pointer items-start gap-3 py-2">
                        <input
                          type="checkbox"
                          checked={typeForm.requiresAdjudication}
                          onChange={(e) => setTypeForm(prev => ({ ...prev, requiresAdjudication: e.target.checked }))}
                          className="checkbox checkbox-warning mt-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="block font-medium">Requires Review</span>
                          <div className="mt-1 break-words text-sm text-base-content/60">Auto-generated consequences of this type will be sent for admin review before parents are notified</div>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Default Type - only for mentions */}
                  {editingCollection === 'mention' && (
                    <div className="space-y-2">
                      <label className="flex cursor-pointer items-start gap-3 py-2">
                        <input
                          type="checkbox"
                          checked={typeForm.isDefault}
                          onChange={(e) => setTypeForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                          className="checkbox checkbox-primary mt-0.5 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="block font-medium">{isRecognitionMode ? 'Default Recognition Type' : 'Default Mention Type'}</span>
                          <div className="mt-1 break-words text-sm text-base-content/60">{isRecognitionMode ? 'Used when no specific type is configured on an achievement' : 'Used when no specific type is configured on an offence'}</div>
                        </div>
                      </label>
                      {!isRecognitionMode && (
                        <label className="flex cursor-pointer items-start gap-3 py-2">
                          <input
                            type="checkbox"
                            checked={typeForm.requiredContextFields.includes('subject')}
                            onChange={(e) => setTypeForm(prev => ({
                              ...prev,
                              requiredContextFields: e.target.checked
                                ? [...new Set([...prev.requiredContextFields, 'subject'])]
                                : prev.requiredContextFields.filter(field => field !== 'subject'),
                            }))}
                            className="checkbox checkbox-primary mt-0.5 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <span className="block font-medium">Require subject</span>
                            <div className="mt-1 break-words text-sm text-base-content/60">Teachers must choose a subject before issuing this Mention type.</div>
                          </div>
                        </label>
                      )}
                    </div>
                  )}

                  <PhaseApplicabilityField schoolId={schoolId} value={typeForm.phaseApplicability}
                    domainApplicability={domain?.phaseApplicability}
                    onChange={phaseApplicability => setTypeForm(previous => ({ ...previous, phaseApplicability }))} />
                  {saveError && <div role="alert" className="text-sm text-error">
                    {saveError.message}
                    {saveError.stale && <button type="button" className="btn btn-ghost btn-sm" onClick={async () => {
                      try {
                        const service = editingCollection === 'mention' ? mentionTypesService : consequenceTypesService;
                        const saved = await service.getById(editingType.id, schoolId);
                        if (!saved || saved.retired) throw new Error('This definition is no longer available.');
                        openModal(saved, editingCollection);
                      } catch (error) { setSaveError({ message: error.message, stale: true }); }
                    }}>Load saved definition</button>}
                  </div>}
                  {/* Description (Optional) */}
                  <div>
                    <label className="label">
                      <span className="label-text font-medium">Description</span>
                    </label>
                    <textarea
                      placeholder="Optional description or notes..."
                      value={typeForm.description}
                      onChange={(e) => setTypeForm(prev => ({ ...prev, description: e.target.value }))}
                      className="textarea textarea-bordered w-full"
                      rows={3}
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex items-center justify-between pt-6">
                    <div>
                      {editingType && (
                        <Button
                          type="button"
                          onClick={() => handleDelete(editingType)}
                          className="btn-error btn-outline"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Retire
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-6">
                      <Button
                        type="button"
                        onClick={closeModal}
                        className="btn-ghost"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="btn-primary"
                      >
                        {editingType ? 'Update' : 'Create'} {embedded && editingCollection !== 'mention' ? 'Action' : 'Type'}
                      </Button>
                    </div>
                  </div>
                  </fieldset>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>, document.body)}

      {/* Icon Picker Modal */}
      {showIconPicker && (
        <IconPicker
          selectedIcon={typeForm.icon}
          onIconSelect={(iconName) => {
            setTypeForm(prev => ({ ...prev, icon: iconName }));
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}

      {/* Color Picker Modal */}
      {showColorPicker && (
        <ColorPicker
          selectedColor={typeForm.color}
          onColorSelect={(color) => {
            setTypeForm(prev => ({ ...prev, color: color }));
            setShowColorPicker(false);
          }}
          onClose={() => setShowColorPicker(false)}
        />
      )}

      <div className={embedded ? '' : 'container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl mt-8'}>
        {!embedded && (
          <div className={`${CSS_CLASSES.CARD_ELEVATED} mb-6`}>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <div className="mb-2">
                  <h1 className="text-2xl sm:text-3xl font-bold text-base-content">
                    {isRecognitionMode ? 'Reward Types Manager' : 'Consequence Types Manager'}
                  </h1>
                </div>
                <p className="text-base-content/70">
                  {isRecognitionMode
                    ? 'Manage reward types used in recognition rules and achievements'
                    : 'Manage consequence types used in discipline rules and queue'}
                </p>
              </div>
              <div className="flex flex-wrap gap-6 flex-shrink-0">
                <Button
                  type="button"
                  onClick={() => openModal()}
                  className={`${CSS_CLASSES.BUTTON_BASE_WITH_SHADOW} btn-primary whitespace-nowrap`}
                >
                  <PlusIcon className="w-5 h-5" />
                  <span className="hidden sm:inline">{isRecognitionMode ? 'Create Reward' : 'Create Type'}</span>
                  <span className="sm:hidden">{isRecognitionMode ? 'Reward' : 'Type'}</span>
                </Button>
              </div>
            </div>

            <div className="mt-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-base-content/50" />
                <Input
                  type="text"
                  placeholder={isRecognitionMode ? "Search reward types..." : "Search consequence types..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-bordered w-full pl-10"
                />
                {searchQuery && (
                  <Button
                    onClick={clearFilters}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-base-content/50 hover:text-base-content"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {showTypes && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h2 className={embedded ? 'text-sm font-semibold text-base-content' : 'text-lg font-bold text-base-content'}>
                {isRecognitionMode ? 'Recognition Types' : 'Mention Types'}
              </h2>
              <Button
                type="button"
                onClick={() => openModal(null, 'mention')}
                className={`${embedded ? 'btn-xs' : 'btn-sm'} btn-primary gap-1 whitespace-nowrap`}
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                {isRecognitionMode ? 'Create Recognition Type' : 'Create Mention Type'}
              </Button>
            </div>
            {filteredMentionTypes.length > 0 ? (
              <div className="card bg-base-100 border border-base-300">
                <div className="p-4 space-y-2">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={filteredMentionTypes.map(t => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <AnimatePresence>
                        {filteredMentionTypes.map(type => (
                          <motion.div
                            key={type.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <SortableConsequenceTypeTag
                              type={type}
                              onClick={() => openModal(type, 'mention')}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            ) : (
              <div className="card bg-base-100 border border-base-300">
                <div className="p-8 text-center text-base-content/50">
                  <p className="text-sm">{isRecognitionMode ? 'No recognition types yet. Click "Create Recognition Type" to add one.' : 'No mention types yet. Click "Create Mention Type" to add one.'}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {showActions && <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className={`${embedded ? 'text-sm font-semibold text-base-content/70' : 'text-lg font-bold text-base-content'}`}>
              {embedded ? 'Actions' : (isRecognitionMode ? 'Reward Types' : 'Consequence Types')}
            </h2>
            {!(embedded && filteredConsequenceTypes.length === 0) && (
              <Button
                type="button"
                onClick={() => openModal(null, 'consequence')}
                className={`${embedded ? 'btn-xs' : 'btn-sm'} btn-primary gap-1 whitespace-nowrap`}
              >
                <PlusIcon className={embedded ? 'w-3.5 h-3.5' : 'w-4 h-4 mr-1'} />
                {embedded
                  ? 'Action'
                  : (isRecognitionMode ? 'Create Reward Type' : 'Create Consequence Type')}
              </Button>
            )}
          </div>
          {filteredConsequenceTypes.length > 0 ? (
            <div className="space-y-3">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredConsequenceTypes.map(t => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <AnimatePresence>
                    {filteredConsequenceTypes.map(type => (
                      <motion.div
                        key={type.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <SortableExpandableConsequenceCard
                          type={type}
                          onEditClick={(t) => openModal(t, 'consequence')}
                          onTypeUpdated={loadTypes}
                          embedded={embedded}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </SortableContext>
              </DndContext>
            </div>
          ) : embedded ? (
            <form onSubmit={handleQuickAdd} className="flex gap-2">
              <Input
                type="text"
                placeholder={isRecognitionMode ? 'Name a reward…' : 'Name an action…'}
                value={quickAddName}
                onChange={(e) => setQuickAddName(e.target.value)}
                className="input-bordered input-sm flex-1"
                autoFocus
              />
              <Button
                type="submit"
                className="btn-primary btn-sm"
                disabled={!quickAddName.trim() || quickAddSubmitting}
              >
                Add
              </Button>
            </form>
          ) : (
            <div className="card bg-base-100 border border-base-300">
              <div className="p-8 text-center text-base-content/50">
                <p className="text-sm">
                  {isRecognitionMode ? 'No reward types yet. Click "Create Reward Type" to add one.' : 'No consequence types yet. Click "Create Consequence Type" to add one.'}
                </p>
              </div>
            </div>
          )}
        </div>}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </>
  );
}
